import React from 'react'
import { useTranslation } from 'react-i18next'
import { BellRing, ClipboardList, EyeOff, FileText, Loader2, UserCheck } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Editor } from '@/components/ui/editor'
import { cn } from '@/lib/utils'
import { sdk } from '@/lib/sdk'
import type { SurveyTemplate } from '@/types/surveys'
import type { SurveyDraft } from './surveysSpaceConfig'
import { RESPONSE_MODES } from './surveyUtils'

/** An empty template text can come back as its own i18n key (BUGS.md SURV-1): treat it as empty. */
const templateText = (v: string | null | undefined) => (v && !v.startsWith('surveys.templates.') ? v : '')

const card = (selected: boolean, disabled: boolean) => cn(
  'flex w-full min-w-0 items-start gap-3 rounded-lg border-2 p-3 text-left transition-all',
  'focus:outline-none focus:ring-2 focus:ring-primary-200 focus:ring-offset-1',
  selected ? 'border-primary bg-primary-50' : 'border-gray-200 bg-white hover:border-gray-300',
  disabled && 'cursor-not-allowed opacity-50'
)

/**
 * The "Next" step of a one-survey space (F-103): title, description, a
 * template (or blank) and the mode. The survey is created as a draft right
 * after the space, and the person lands in its editor.
 */
export const SurveySingleStep: React.FC<{
  draft: SurveyDraft
  onChange: (draft: SurveyDraft) => void
  titleError?: string
  disabled?: boolean
}> = ({ draft, onChange, titleError, disabled = false }) => {
  const { t } = useTranslation(['surveys', 'common'])
  const [templates, setTemplates] = React.useState<SurveyTemplate[] | null>(null)
  const [failed, setFailed] = React.useState(false)
  /* The rich editor keeps the callback it was born with: it reads the latest draft through this. */
  const latest = React.useRef({ draft, onChange })
  latest.current = { draft, onChange }

  React.useEffect(() => {
    let canceled = false
    sdk.surveys.templates()
      .then((list) => { if (!canceled) setTemplates(list.filter((x) => x.key !== 'blank')) })
      .catch(() => { if (!canceled) setFailed(true) })
    return () => { canceled = true }
  }, [])

  /* A template fills what the person has not written yet (or what the previous template wrote). */
  const pick = (key: string | null) => {
    const previous = templates?.find((x) => x.key === draft.template)
    const next = templates?.find((x) => x.key === key)
    const untouched = (value: string, before: string | null | undefined) => !value.trim() || value === templateText(before)
    onChange({
      ...draft,
      template: key,
      title: untouched(draft.title, previous?.title) ? templateText(next?.title) : draft.title,
      description: untouched(draft.description, previous?.description) ? templateText(next?.description) : draft.description,
      anonymity: next?.anonymity ?? draft.anonymity
    })
  }

  return (
    <div className="min-w-0 space-y-5" data-testid="survey-single-step">
      <div className="space-y-1">
        <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-900">{t('surveys:wizard.step')}</h3>
        <p className="text-xs text-gray-500">{t('surveys:wizard.stepHint')}</p>
      </div>

      <div className="space-y-2">
        <span className="text-sm font-medium text-gray-700">{t('surveys:wizard.template')}</span>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          <button type="button" aria-pressed={draft.template === null} data-template="blank" disabled={disabled}
            className={card(draft.template === null, disabled)} onClick={() => pick(null)}>
            <FileText className={cn('mt-0.5 h-5 w-5 flex-shrink-0', draft.template === null ? 'text-primary' : 'text-gray-400')} />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-gray-900">{t('surveys:templates.blank')}</span>
              <span className="mt-0.5 block text-xs text-gray-500">{t('surveys:templates.blankHint')}</span>
            </span>
          </button>
          {(templates ?? []).map((tpl) => {
            const questions = tpl.sections.reduce((n, s) => n + s.questions.length, 0)
            const selected = draft.template === tpl.key
            return (
              <button key={tpl.key} type="button" aria-pressed={selected} data-template={tpl.key} disabled={disabled}
                className={card(selected, disabled)} onClick={() => pick(tpl.key)}>
                <ClipboardList className={cn('mt-0.5 h-5 w-5 flex-shrink-0', selected ? 'text-primary' : 'text-gray-400')} />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-gray-900">{templateText(tpl.title) || tpl.key}</span>
                  <span className="mt-0.5 block text-xs text-gray-500">
                    {tpl.sections.length > 1
                      ? t('surveys:intro.stepsAndQuestions', { steps: tpl.sections.length, count: questions })
                      : t('surveys:intro.questions', { count: questions })}
                    {' · '}{t(`surveys:modes.${tpl.anonymity}`)}
                  </span>
                </span>
              </button>
            )
          })}
          {!templates && !failed && (
            <div className="flex items-center justify-center rounded-lg border-2 border-dashed border-gray-200 p-3">
              <Loader2 className="h-5 w-5 animate-spin text-gray-400" aria-label={t('common:loading')} />
            </div>
          )}
        </div>
        {failed && <p className="text-xs text-amber-700">{t('surveys:templates.loadError')}</p>}
      </div>

      <div className="grid gap-2">
        <label htmlFor="single-survey-title" className="text-sm font-medium text-gray-700">
          {t('surveys:wizard.title')} <span className="text-red-500">*</span>
        </label>
        <Input id="single-survey-title" value={draft.title} disabled={disabled} maxLength={200} aria-invalid={Boolean(titleError)}
          onChange={(e) => onChange({ ...draft, title: e.target.value })} placeholder={t('surveys:wizard.titlePlaceholder')} />
        {titleError && <p className="text-xs text-red-600">{titleError}</p>}
      </div>

      <div className="grid gap-2">
        <label className="text-sm font-medium text-gray-700">{t('surveys:wizard.description')}</label>
        {/* Remounted per template: the editor ignores an empty `content`, so going back to blank would leave the old text on screen. */}
        <Editor aiFieldKind="survey" key={draft.template ?? 'blank'} content={draft.description}
          onChange={(description) => latest.current.onChange({ ...latest.current.draft, description })} className="min-h-[110px] sm:min-h-[140px]" />
      </div>

      <div className="space-y-2">
        <span className="text-sm font-medium text-gray-700">{t('surveys:wizard.anonymity')}</span>
        <div className="grid gap-2 sm:grid-cols-2">
          {(['IDENTIFIED', 'ANONYMOUS'] as const).map((mode) => {
            const selected = draft.anonymity === mode
            const Icon = mode === 'ANONYMOUS' ? EyeOff : UserCheck
            return (
              <button key={mode} type="button" aria-pressed={selected} data-anonymity={mode} disabled={disabled}
                className={card(selected, disabled)} onClick={() => onChange({ ...draft, anonymity: mode })}>
                <Icon className={cn('mt-0.5 h-5 w-5 flex-shrink-0', selected ? 'text-primary' : 'text-gray-400')} />
                <span className="min-w-0">
                  <span className={cn('block text-sm font-medium', selected ? 'text-primary-700' : 'text-gray-900')}>{t(`surveys:modes.${mode}`)}</span>
                  <span className="mt-0.5 block text-xs leading-snug text-gray-500">{t(`surveys:modes.${mode}Hint`)}</span>
                </span>
              </button>
            )
          })}
        </div>
      </div>

      <div className="space-y-2" data-testid="survey-response-mode-choice">
        <span className="text-sm font-medium text-gray-700">{t('surveys:responseMode.label')}</span>
        <div className="grid gap-2 sm:grid-cols-3">
          {RESPONSE_MODES.map((mode) => {
            const selected = draft.responseMode === mode
            return (
              <button key={mode} type="button" aria-pressed={selected} data-response-mode={mode} disabled={disabled}
                className={card(selected, disabled)} onClick={() => onChange({ ...draft, responseMode: mode })}>
                <BellRing className={cn('mt-0.5 h-5 w-5 flex-shrink-0', selected ? 'text-primary' : 'text-gray-400')} />
                <span className="min-w-0">
                  <span className={cn('block text-sm font-medium', selected ? 'text-primary-700' : 'text-gray-900')}>{t(`surveys:responseMode.${mode}`)}</span>
                  <span className="mt-0.5 block text-xs leading-snug text-gray-500">{t(`surveys:responseMode.${mode}Hint`)}</span>
                </span>
              </button>
            )
          })}
        </div>
      </div>

      <p className="text-xs text-gray-500">{t('surveys:wizard.bornDraft')}</p>
    </div>
  )
}
